import { IonContent, IonPage } from "@ionic/react";
import React, { useEffect } from "react";
import { Screens, useAnalytics } from "../AppAnalyticsProvider";
import { useHistory } from "react-router";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import CustomHelmet from "../components/ComponentsFunctional/CustomHelmet/CustomHelmet";
import { LegalDocumentKey, legalDocuments } from "../content/legal";

interface LegalPageProps {
    document: LegalDocumentKey;
}

const LegalPage: React.FC<LegalPageProps> = ({ document }) => {
    const history = useHistory();
    const doc = legalDocuments[document];
    const { trackScreen } = useAnalytics();

    // One screen name for all legal docs; the doc key (privacy/terms/eula)
    // rides along as screen_class so they can still be split in GA4.
    useEffect(() => {
        trackScreen(Screens.LEGAL, document);
    }, [trackScreen, document]);

    const handleBack = () => {
        if (history.length > 1) {
            history.goBack();
        } else {
            history.push("/");
        }
    };

    return (
        <IonPage>
            <CustomHelmet
                title={`${doc.title} — Ecliptic`}
                description={`${doc.title} for Ecliptic, the app for park skiers and snowboarders serious about progression.`}
                url={`https://ecliptic.day${doc.path}`}
            />
            <IonContent>
                <div className="min-h-full bg-black text-white font-['Mukta_Mahee']">
                    <nav className="sticky top-0 z-50 bg-[rgba(0,0,0,0.75)] backdrop-blur-[24px] border-b border-[rgba(88,97,172,0.18)]">
                        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
                            <button
                                type="button"
                                onClick={handleBack}
                                className="text-white/65 hover:text-white text-sm font-bold tracking-[0.4px] transition-colors duration-200"
                                aria-label="Go back"
                            >
                                ← Back
                            </button>
                            <img
                                src="/assets/logo/ECLIPTIC_NEW_WHITE_PNG_NO_PADDING.png"
                                alt="Ecliptic"
                                className="h-[26px] w-auto object-contain block"
                            />
                        </div>
                    </nav>

                    <article className="max-w-3xl mx-auto px-6 py-12">
                        <h1 className="text-3xl sm:text-4xl font-bold mb-8 tracking-tight">
                            {doc.title}
                        </h1>
                        <div className="prose prose-invert prose-headings:font-bold prose-a:text-electric-blue prose-a:no-underline hover:prose-a:underline max-w-none">
                            <ReactMarkdown remarkPlugins={[remarkGfm]}>
                                {doc.content}
                            </ReactMarkdown>
                        </div>
                    </article>

                    <footer className="max-w-3xl mx-auto px-6 py-10 border-t border-white/10 mt-12">
                        <div className="flex flex-wrap gap-6 text-sm text-white/50">
                            <a href="/privacy" className="hover:text-white transition-colors">Privacy Policy</a>
                            <a href="/terms" className="hover:text-white transition-colors">Terms of Service</a>
                            <a href="/eula" className="hover:text-white transition-colors">EULA</a>
                            <a href="/" className="hover:text-white transition-colors ml-auto">Home</a>
                        </div>
                    </footer>
                </div>
            </IonContent>
        </IonPage>
    );
};

export default LegalPage;
